<script>
  /**
   * StorefrontHeader Component
   * Displays and manages the header section of a storefront, including:
   * - Storefront title and actions
   * - Font list management
   * - Change tracking
   * - Missing font detection
   */
  import { _ } from 'svelte-i18n';
  import { createEventDispatcher } from 'svelte';
  import { derived, writable } from 'svelte/store';
  import { scale } from 'svelte/transition';
  import { previews_tracking, previews_font_size, previews_sort_order, flash_content, text_entered, user_storefronts } from '../stores';
  import { saveStorefrontData } from '../services/storefronts';
  import Button from '../elements/Button.svelte'

  export let selectedStorefront
  export let storefront_fonts
  const dispatch = createEventDispatcher();
  
  // Create a reactive store from the passed fonts prop
  const storefrontFontsStore = writable(storefront_fonts);
  $: storefrontFontsStore.set(storefront_fonts)  

  // Create a writable store to track the original description
  const originalDescription = writable('');
  
  // Create a writable store to track the original name
  const originalName = writable('');
  
  // Track the current storefront ID to detect when storefront changes
  let currentStorefrontId = null;
  
  // Update these stores when the selectedStorefront changes
  $: if ($selectedStorefront) {
    // Set the original values when storefront changes or when first loaded
    if (currentStorefrontId !== $selectedStorefront.id) {
      console.log('Setting original values for new storefront:', $selectedStorefront.name, $selectedStorefront.description);
      originalName.set($selectedStorefront.name || '');
      originalDescription.set($selectedStorefront.description || '');
      currentStorefrontId = $selectedStorefront.id;
    }
  }

  /**
   * Compares current fonts with storefront fonts to detect changes
   * Uses postscript_name as unique identifier
   * Returns object with removed and renamed fonts
   */
  function compareFonts($selectedStorefront, storefront_fonts) {
    if (!$selectedStorefront) return { removed: [], renamed: [] };
    
    const storefrontFonts = JSON.parse($selectedStorefront.fonts);
    const currentFonts = storefront_fonts;
    
    // Track removed fonts
    const removed = storefrontFonts.filter(sf => 
      !currentFonts.some(cf => cf.postscript_name === sf.postscript_name)
    ).map(f => f.postscript_name);
    
    // Track renamed fonts by comparing with storefronts array
    const renamed = currentFonts.filter(cf => {
      const storefrontEntry = cf.storefronts?.find(sf => sf.id === $selectedStorefront.id);
      const originalFont = storefrontFonts.find(sf => sf.postscript_name === cf.postscript_name);
      return originalFont && storefrontEntry && storefrontEntry.preferred_name !== originalFont.preferred_name;
    }).map(f => ({
      postscript_name: f.postscript_name,
      from: storefrontFonts.find(sf => sf.postscript_name === f.postscript_name).preferred_name,
      to: f.storefronts.find(sf => sf.id === $selectedStorefront.id).preferred_name
    }));
    
    return { removed, renamed };
  }

  function getSortOrderDisplay(order) {
    switch(order) {
      case 'ascending': return 'A to Z';
      case 'descending': return 'Z to A';
      case 'random': return 'random';
      default: return order;
    }
  }

  // Helper function to normalize text for comparison
  function normalizeText(text) {
    if (!text) return '';
    // First, replace all consecutive spaces with a single space (preserve linebreaks)
    const preserveLinebreaks = text.replace(/[^\S\n]+/g, ' ');
    // Then normalize consecutive linebreaks
    const normalizedLinebreaks = preserveLinebreaks.replace(/\n+/g, '\n');
    // Finally, trim leading/trailing whitespace
    return normalizedLinebreaks.trim();
  }

  /**
   * Derived store that tracks all pending changes including:
   * - Font size changes
   * - Tracking (letter-spacing) changes
   * - Sort order changes
   * - Font list changes (removed/renamed)
   * - Default text changes
   * - Description changes
   * - Name changes
   */
  const pendingChanges = derived(
    [selectedStorefront, previews_tracking, previews_font_size, previews_sort_order, storefrontFontsStore, text_entered, originalDescription, originalName],
    ([$selectedStorefront, $previews_tracking, $previews_font_size, $previews_sort_order, $storefrontFonts, $text_entered, $originalDescription, $originalName]) => {
      if (!$selectedStorefront) return [];
      
      const changes = [];
      
      // Track existing value changes
      if ($selectedStorefront.tracking !== $previews_tracking) {
        changes.push($_('storefronts.' + ($previews_tracking > $selectedStorefront.tracking ? 'letterSpacingIncreased' : 'letterSpacingDecreased')));
      }
      if ($selectedStorefront.font_scale !== $previews_font_size) {
        changes.push($_('storefronts.' + ($previews_font_size > $selectedStorefront.font_scale ? 'fontSizeIncreased' : 'fontSizeDecreased')));
      }
      if ($selectedStorefront.sort_order !== $previews_sort_order) {
        changes.push($_('storefronts.sortOrderChange', { values: { from: getSortOrderDisplay($selectedStorefront.sort_order), to: getSortOrderDisplay($previews_sort_order) } }));
      }
      
      // Track text changes
      if ($selectedStorefront.default_text !== $text_entered) {
        changes.push($_('storefronts.defaultTextChanged'));
      }
      
      // Track description changes using normalized text comparison
      if (normalizeText($selectedStorefront.description) !== normalizeText($originalDescription)) {
        changes.push($_('storefronts.descriptionChanged'));
      }
      
      // Track name changes using normalized text comparison
      if (normalizeText($selectedStorefront.name) !== normalizeText($originalName)) {
        changes.push($_('storefronts.nameChanged'));
      }
      
      // Track font changes (only removed and renamed)
      const fontChanges = compareFonts($selectedStorefront, $storefrontFonts);
      
      if (fontChanges.removed.length) {
        changes.push($_('storefronts.removedFonts', { values: { fonts: fontChanges.removed.join(', ') } }));
      }
      fontChanges.renamed.forEach(({from, to}) => {
        changes.push($_('storefronts.renamedFont', { values: { from, to } }));
      });
      
      return changes;
    }
  );

  // Simplified derived store just to check if there are any changes
  const hasChanges = derived(
    [pendingChanges],
    ([$pendingChanges]) => $pendingChanges.length > 0
  );

  // Dispatch ppending changes true or false to parent component
  $: dispatch('pendingChanges', $hasChanges);

  /**
   * Tracks fonts that are in the storefront but missing their font files
   * Used to show upload prompt for missing fonts
   */


  // Sync storefront display settings with preview settings
  $: if ($selectedStorefront) {
    $previews_tracking = $selectedStorefront.tracking;
    $previews_font_size = $selectedStorefront.font_scale;
    $previews_sort_order = $selectedStorefront.sort_order;
  }

  /**
   * Saves current state of the storefront including:
   * - Removed fonts
   * - Display settings (tracking, font size, sort order)
   * - Default text
   * - Description
   * - Name
   * Only processes changes that actually occurred
   */
  async function save() {
    if ($selectedStorefront) {
      const fontChanges = compareFonts($selectedStorefront, $storefrontFontsStore);
      
      let fontsToRemove = [];
      let fontsToRename = [];

      // Process removed fonts
      if (fontChanges.removed?.length) {
        const storefrontFonts = JSON.parse($selectedStorefront.fonts);
        fontsToRemove = storefrontFonts
          .filter(f => fontChanges.removed.includes(f.postscript_name))
          .map(f => ({
            preferred_name: f.preferred_name,
            postscript_name: f.postscript_name
          }));
      }

      // Process renamed fonts
      if (fontChanges.renamed?.length) {
        fontsToRename = fontChanges.renamed.map(({postscript_name, to}) => ({
          postscript_name,
          preferred_name: to
        }));
      }

      // Save changes to server
      await saveStorefrontData({
        storefront_id: $selectedStorefront.id,
        fonts_to_remove: fontsToRemove,
        fonts_to_rename: fontsToRename,
        default_text: $text_entered !== $selectedStorefront.default_text ? $text_entered : undefined,
        description: $selectedStorefront.description !== $originalDescription ? $selectedStorefront.description : undefined,
        name: $selectedStorefront.name !== $originalName ? $selectedStorefront.name : undefined,
        save_view_options: true
      });

      // Update the original description and name after saving
      originalDescription.set($selectedStorefront.description);
      originalName.set($selectedStorefront.name);
      
      // Update the user_storefronts store with the new values
      if ($text_entered !== $selectedStorefront.default_text) {
        user_storefronts.update(storefronts => {
          return storefronts.map(sf => {
            if (sf.id === $selectedStorefront.id) {
              return { ...sf, default_text: $text_entered };
            }
            return sf;
          });
        });
      }
      
      $flash_content = `Changes saved to "${$selectedStorefront.name}"`;
    }
  }
  
  // Reset original values when selectedStorefront changes or component is destroyed
  $: if (!$selectedStorefront) {
    originalDescription.set('');
    originalName.set('');
    currentStorefrontId = null;
  }
</script>

{#if $selectedStorefront}
  <div class="button_wrapper">
    <Button on:click={save} disabled={!$hasChanges}>Save Changes</Button>
  </div>
  {#if $pendingChanges.length > 0}
    <div transition:scale={{start: 0.9}} class="pending-changes">
      <span class="label">{$_('modals.editStorefront.pendingChanges')}</span>
      <ul>
        {#each $pendingChanges as change}
          <li>{change}</li>
        {/each}
      </ul>
    </div>
  {/if}
{/if}

<style>

  .pending-changes .label {
    font-size: 1.2rem;
    font-weight: bold;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: var(--c-text-faded);
  }

  .pending-changes {
    z-index: 1;
    position: absolute;
    top: 0rem;
    right: 0rem;
    padding: 5.4rem 1rem 1rem 1.5rem;
    width: 37rem;
    color: var(--c-warning-text);
    text-align: left;
    border: 1px solid var(--c-bg-a2);
    border-left-color: var(--c-pill-bg);
    border-bottom-color: var(--c-pill-bg);
    background-color: var(--c-warning-bg);
    border-radius: 1rem;
    transform-origin: top right;
  }
  .pending-changes li {
    margin: 0.25rem 0 0.25rem 1.5rem;
    list-style: disc;
    font-size: 1.5rem;
    color: var(--c-warning-text);
  }
  
  .pending-changes .label {
    display: inline-block;
    margin-right: 0.5rem;
    color: var(--c-warning-text);
  }

  .button_wrapper {
    z-index: 2;
  }
</style>